import { getAddressDataFromPlaceDetails } from '@choco-admin/utils';
import { ChangeEvent, useCallback, useRef, useState, useEffect } from 'react';

import type { AddressChangeHandler, AddressData } from '../types';

import type { PlaceDetails, PlacesUnavailableReason } from './useGooglePlaces';
import { useGooglePlaces } from './useGooglePlaces';

// Local interface for Google Places predictions
interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface FormattedPrediction {
  placeId: string;
  description: string;
}

interface UseAddressInputProps {
  onAddressChange: AddressChangeHandler<AddressData>;
  isCustom?: boolean;
  onManualInput?: (value: string) => void;
  /** Current input value — used to seed the custom-buyer name (#106). */
  value?: string;
}

const formatPrediction = ({
  place_id: placeId,
  description,
}: PlacePrediction): FormattedPrediction => ({
  placeId,
  description,
});

const predictionsConfig = {
  placeTypes: ['address'],
};

/**
 * Each failure needs a different reaction, so say which one it is.
 *
 * None of these offer a custom buyer as a way out. A custom buyer takes its
 * address from the same address search, so when the search is unavailable that
 * route is unavailable too. Suggesting it would send the operator to a dead end.
 */
const PLACES_ERROR_MESSAGES: Record<PlacesUnavailableReason, string> = {
  auth: 'The address search is not available in this portal. It needs a fix from an engineer, so report it rather than retrying.',
  unavailable:
    'The address search could not load. Check your connection and try again.',
  timeout: 'Google did not respond in time. Try again.',
  request: 'Google could not complete that search. Try a different wording.',
};

export const useAddressInput = ({
  isCustom = false,
  onAddressChange,
  onManualInput,
  value: currentValue,
}: UseAddressInputProps) => {
  const [predictionsVisible, setPredictionsVisible] = useState(false);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const predictionsListRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close predictions
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      inputContainerRef.current &&
      !inputContainerRef.current.contains(event.target as Node)
    ) {
      setPredictionsVisible(false);
    }
  }, []);

  const showPredictions = useCallback(() => {
    setPredictionsVisible(true);
    document.addEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const hidePredictions = useCallback(() => {
    setPredictionsVisible(false);
    document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  // Place details handlers
  const onPlaceDetailsRequestSuccess = useCallback(
    (placeDetails: PlaceDetails) => {
      const addressData = getAddressDataFromPlaceDetails(
        placeDetails,
        isCustom
      );
      onAddressChange(addressData);
      // Close only now that an address actually arrived — see below.
      hidePredictions();
    },
    [onAddressChange, isCustom, hidePredictions]
  );

  const onPlaceDetailsRequestError = useCallback(
    (error: Error) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching place details:', error);
      // The dropdown is where progress and failures are rendered, so it has to
      // be open for the operator to learn that the selection did not take.
      showPredictions();
    },
    [showPredictions]
  );

  // Use the Google Places hook
  const { predictions, ...placesInfo } = useGooglePlaces({
    onPlaceDetailsRequestSuccess,
    onPlaceDetailsRequestError,
  });

  const predictionsErrorMessage = placesInfo.predictionsError
    ? PLACES_ERROR_MESSAGES[placesInfo.predictionsError]
    : undefined;

  const formattedPredictions = predictions.map(formatPrediction);

  const handleInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      // Call manual input handler if provided
      if (onManualInput) {
        onManualInput(value);
      }

      placesInfo.fetchPlacePredictions({
        searchTerm: value,
        ...(isCustom && predictionsConfig),
      });

      if (value.trim()) {
        showPredictions();
      } else {
        hidePredictions();
      }
    },
    [isCustom, placesInfo, showPredictions, hidePredictions, onManualInput]
  );

  const onGooglePlaceSelect = useCallback(
    ({ placeId }: FormattedPrediction) => {
      // Deliberately stays open until the details call settles: closing here
      // hid both the spinner and any failure, so a selection that never
      // completed looked like nothing happened at all.
      placesInfo.fetchPlaceDetails({ placeId });
    },
    [placesInfo]
  );

  const onCreateCustomBuyerClick = useCallback(() => {
    // Explicitly creating a custom buyer seeds the name from the search text
    // — typing alone never touches the name field (#106).
    onAddressChange({
      addressSource: 'custom',
      fullAddress: '',
      name: (currentValue ?? '').trim(),
    });
    hidePredictions();
  }, [onAddressChange, hidePredictions, currentValue]);

  // Clean up event listener on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return {
    ...placesInfo,
    predictionsErrorMessage,
    predictionsAreVisible: predictionsVisible,
    predictions: formattedPredictions,
    handleInputChange,
    onGooglePlaceSelect,
    onCreateCustomBuyerClick,
    inputContainerRef,
    predictionsListRef,
    showPredictions,
    hidePredictions,
  };
};
